/**
 * Utilities for accessing API endpoints and receiving fully typed responses
 */

import { isAbortError } from "@js/util/errorUtils";
import { downloadResponse, parseSseEvent } from "./fetchUtils";

/**
 * Metadata typically for API responses using a cursor for pagination
 * Includes the total count of items and cursor for pagination
 */
export type Metadata = {
    count: number;
    cursor: {
        current: string | null;
        next: string | null;
    };
    url?: string;
};

/**
 * Typical params that may be available when loading a url
 */
export enum SearchParam {
    SELECTED = "selected",
    SORT_BY = "sortBy",
    ORDER = "order",
    LIMIT = "limit",
    CURSOR = "cursor",
}

/**
 * Metadata typically for API responses using page-based pagination
 */
export type PaginationMeta = {
    count: number;
    pagination: {
        limit: number;
        pages: number;
        current: number;
    };
};

type ApiBaseResponseSuccess<TRespData> = {
    success: true;
    data: TRespData;
};

type ApiBaseResponseFailure = {
    success: false;
    message: string; // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/message
    name?: string; // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Error/name
    data?: unknown;
};

// For unknown failures, such as if we can't parse the response
// In this case, we provide the original status code as an extra field in case we need to handle it
interface ApiBaseResponseUnknownFailure extends ApiBaseResponseFailure {
    responseStatus: number;
    detail: unknown;
}

export type ApiBaseResponse<TRespData> =
    | ApiBaseResponseSuccess<TRespData>
    | ApiBaseResponseFailure
    | ApiBaseResponseUnknownFailure;

/** The base type for an API request body */
type ApiRequestBodyBase = Record<string, unknown> | FormData;

interface ApiRequestOptions<TBody extends ApiRequestBodyBase | undefined = undefined> {
    /**
     * The API endpoint to send the request to
     */
    url: string;
    /**
     * The HTTP method to use for the request
     */
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
    /**
     * The body of the request, if applicable
     * For a GET request, this is transformed into query parameters where possible
     */
    body?: TBody;
    /**
     * Optional signal to abort the request
     */
    signal?: AbortSignal;
}

interface DownloadApiRequestOptions<
    TBody extends ApiRequestBodyBase | undefined = undefined,
> extends ApiRequestOptions<TBody> {
    /**
     * Expected content types of the response, e.g. text/csv
     */
    accept: string[];
    /**
     * Filename to use for the download
     */
    filename: string;
}

export type SseMessageEvent<TEventType extends string = string, TMessageData = unknown> = {
    eventType: TEventType | undefined;
    data: ApiBaseResponse<TMessageData>;
};

export type SseMessageCallback<TMessageEvent extends SseMessageEvent> = (event: TMessageEvent) => void;

interface SseApiRequestOptions<
    TMessageEvent extends SseMessageEvent,
    TBody extends ApiRequestBodyBase | undefined = undefined,
> extends ApiRequestOptions<TBody> {
    /**
     * The callback to handle each message from the server
     */
    onMessage: SseMessageCallback<TMessageEvent>;
    /**
     * The expected type of the final response
     * This will determine when the stream terminates and should have the final response included
     * If the request terminates without this, an error will be returned
     * @default "complete"
     */
    finalEventType?: string;
}

/**
 * Build a fetch request to an API endpoint
 * @param accept - The Accept header to send with the request
 * @param options - The options for the request
 * @returns - A promise that resolves to the response from the server
 */
async function buildRequest(
    accept: string[],
    options: ApiRequestOptions<ApiRequestBodyBase | undefined>,
): Promise<Response> {
    let { url } = options;

    const requestOptions: RequestInit = {
        method: options.method,
        headers: {
            "Content-Type": "application/json",
            Accept: accept.join(", "),
        },
        signal: options.signal,
    };

    if (options.body instanceof FormData) {
        // If the body is a FormData, we don't set Content-Type, as the browser will set it with the correct boundary
        requestOptions.body = options.body;
        if (requestOptions.headers && "Content-Type" in requestOptions.headers) {
            delete requestOptions.headers["Content-Type"];
        }
    } else if (options.body !== undefined) {
        if (options.method === "GET") {
            // Remove any undefined values from the query string
            const filteredBody = Object.fromEntries(
                Object.entries(options.body as Record<string, string | string[]>).filter(
                    ([, value]) => value !== undefined && value !== null,
                ),
            );

            const queryParams = new URLSearchParams();

            Object.entries(filteredBody).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    // If the value is an array, append each value separately with the same key
                    // The backend will parse a key with [] as an array (e.g. key[]=value1&key[]=value2)
                    value.forEach((v) => queryParams.append(`${key}[]`, v.toString()));
                } else {
                    queryParams.append(key, value.toString());
                }
            });

            const queryString = queryParams.toString();
            url += url.includes("?") ? `&${queryString}` : `?${queryString}`;
        } else {
            // All other methods send as JSON body
            requestOptions.body = JSON.stringify(options.body);
        }
    }

    return fetch(url, requestOptions);
}

/**
 * Parses a JSON response from the server and validates that it matches the expected format (any object)
 * If it does not match, or it fails to parse, a failure response is returned in its place
 * @template TResponseData - The expected type of the response from the server
 * @param text - The response from the server as a string
 * @param status - The HTTP status code of the response
 * @param contentType - The content type of the response (e.g. "application/json")
 * @param eventType - When parsing a text/event-stream, the event type that is associated with the data
 * @returns - The parsed response, always extending ApiBaseResponse
 */
function parseResponse<TResponseData>(
    text: string,
    status: number,
    contentType: string = "",
    eventType?: string,
): ApiBaseResponse<TResponseData> {
    let responseJSON: TResponseData;

    /* If the response is a 200 and HTML, then we probably got given the offline page, return a more meaningful message instead */
    if (status === 200 && contentType.includes("text/html")) {
        const failureResponse: ApiBaseResponseUnknownFailure = {
            success: false,
            responseStatus: status,
            message: "You appear to be offline, check your internet connection and try again.",
            detail: null,
        };
        return failureResponse;
    }

    if (status === 204 && text === "") {
        // 204 No Content, so we return null data
        return {
            success: true,
            data: null as unknown as TResponseData,
        };
    }

    try {
        responseJSON = JSON.parse(text) as TResponseData;
    } catch (e) {
        const failureResponse: ApiBaseResponseUnknownFailure = {
            success: false,
            responseStatus: status,
            message: "An unexpected error occurred",
            detail: e,
            data: text,
        };
        return failureResponse;
    }

    /* Does the response match the expected format? For now this is just any object
     * as most of our endpoints don't return any field to indicate success or failure
     */
    if (typeof responseJSON !== "object" || responseJSON === null) {
        const failureResponse: ApiBaseResponseUnknownFailure = {
            success: false,
            responseStatus: status,
            message: "Invalid response format",
            detail: text,
            data: responseJSON,
        };
        return failureResponse;
    }

    /* Is this not a successful response? If so, it theoretically should be following RFC 9457 "API Problem" */
    const validStatus = status >= 200 && status < 300;
    if (!validStatus || eventType === "error") {
        const failureResponse: ApiBaseResponseFailure = {
            success: false,
            message: "An unexpected error occurred",
            data: responseJSON,
        };
        if ("title" in responseJSON && typeof responseJSON.title === "string") {
            failureResponse.message = responseJSON.title;
        }
        if ("detail" in responseJSON && typeof responseJSON.detail === "string") {
            failureResponse.message += `: ${responseJSON.detail}`;
        }
        return failureResponse;
    }

    const successResponse: ApiBaseResponseSuccess<TResponseData> = {
        success: true,
        data: responseJSON,
    };
    return successResponse;
}

/**
 * Generic function to make an API request to the server
 * Validates that the response matches our expected format
 * API errors should follow RFC 9457 "API Problem"
 * @template TResponseData - The type of the response from the server
 * @template TBody - The type of the request body
 * @param options - Options for the API request
 * @returns - A typed response from the server including a success state
 */
export async function apiRequest<
    TResponseData,
    TBody extends ApiRequestBodyBase | undefined = ApiRequestBodyBase | undefined,
>(options: ApiRequestOptions<TBody>): Promise<ApiBaseResponse<TResponseData>> {
    const request = buildRequest(["application/json"], options);

    let response: Response;
    try {
        response = await request;
    } catch (e) {
        if (!isAbortError(e)) {
            // we only want to log non-abort errors
            console.error("Failed to make API request", e, options);
        }
        const failureResponse: ApiBaseResponseUnknownFailure = {
            success: false,
            responseStatus: 0,
            message: e instanceof Error ? e.message : "An unexpected error occurred",
            name: e instanceof Error ? e.name : "Error",
            detail: e,
        };
        return failureResponse;
    }

    const contentType = response.headers.get("Content-Type") || "";
    const responseBody = await response.text();
    const responseJSON = parseResponse<TResponseData>(responseBody, response.status, contentType);

    return responseJSON;
}

/**
 * Generic function to make an API request and download the response as a file
 * Validates that error responses still match our expected format
 * @template TBody - The type of the request body
 * @param options - Options for the API request, plus an optional fallback filename
 * @returns - A typed response from the server including a success state
 */
export async function downloadApiRequest<TBody extends ApiRequestBodyBase | undefined = ApiRequestBodyBase | undefined>(
    options: DownloadApiRequestOptions<TBody>,
): Promise<ApiBaseResponse<Blob | null>> {
    /** Include application/json in the Accept header to get the right error format */
    const request = buildRequest(["application/json", ...options.accept], options);

    let response: Response;
    try {
        response = await request;
    } catch (e) {
        if (!isAbortError(e)) {
            // we only want to log non-abort errors
            console.error("Failed to make API request", e, options);
        }
        const failureResponse: ApiBaseResponseUnknownFailure = {
            success: false,
            responseStatus: 0,
            message: e instanceof Error ? e.message : "An unexpected error occurred",
            name: e instanceof Error ? e.name : "Error",
            detail: e,
        };
        return failureResponse;
    }

    const contentType = response.headers.get("Content-Type") || "";
    if (response.status === 204) {
        return parseResponse<null>("", response.status, contentType);
    }

    if (!options.accept.some((type) => contentType.includes(type)) || !response.ok) {
        const responseBody = await response.text();
        return parseResponse<null>(responseBody, response.status, contentType);
    }

    const blob = await downloadResponse(response, options.filename);
    return {
        success: true,
        data: blob,
    };
}

/**
 * Generic function to make an API request to the server
 * Validates that the response matches our expected format
 * API errors should follow RFC 9457 "API Problem", sending an event with the type "error" will be treated like this
 * Requires the final message to match options.finalEventType, which will terminate the stream
 * @template TMessageEvent - The type of the response from the server for each message event
 * @template TResponseData - The type of the response from the server for the final message
 * @template TBody - The type of the request body
 * @param options - Options for the API request
 * @returns - A typed response from the server including a success state
 */
export async function sseApiRequest<
    TMessageEvent extends SseMessageEvent,
    TResponseData,
    TBody extends ApiRequestBodyBase | undefined = undefined,
>(options: SseApiRequestOptions<TMessageEvent, TBody>): Promise<ApiBaseResponse<TResponseData>> {
    /** include application/json in a text/event-stream request to get the right error format */
    const request = buildRequest(["application/json", "text/event-stream"], options);

    let response: Response;
    try {
        response = await request;
    } catch (e) {
        if (!isAbortError(e)) {
            // we only want to log non-abort errors
            console.error("Failed to make API request", e, options);
        }
        const failureResponse: ApiBaseResponseUnknownFailure = {
            success: false,
            responseStatus: 0,
            message: e instanceof Error ? e.message : "An unexpected error occurred",
            detail: e,
        };
        return failureResponse;
    }

    // If the response is a 200 and HTML, then we probably got given the offline page, return a more meaningful message instead
    // If there's no body (which can happen in some edge cases), return an unknown failure
    const contentType = response.headers.get("Content-Type") || "";
    if (!response.body || (response.status === 200 && contentType.includes("text/html"))) {
        return parseResponse<TResponseData>("", response.status, contentType);
    }

    // If the content type is json, handle as a normal request (it likely errored early)
    // depending on where the error was, the server may have provided a proper json-formatted error
    // otherwise, we'll just return an unknown failure like we usually do
    if (response.headers.get("content-type")?.includes("application/json")) {
        const text = await response.text();
        return parseResponse<TResponseData>(text, response.status);
    }

    // Parse the text/event-stream response as it comes in until it's closed or errors
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");

    let buffer = "";
    let finalResponseData: string | undefined;
    while (finalResponseData === undefined) {
        const { done, value } = await reader.read();

        // If the stream ended normally
        if (done) {
            break;
        }

        // Decode the chunk into text and split it into individual events (always by \n\n)
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");

        // Process all complete events except possibly the last partial
        for (let i = 0; i < events.length - 1; i += 1) {
            const rawEvent = events[i].trim();
            if (rawEvent) {
                const { eventType, data } = parseSseEvent(rawEvent);

                if (eventType === (options.finalEventType ?? "complete")) {
                    // If the server sends the final event, close the stream
                    finalResponseData = data;
                    break;
                }

                const parsedData: TMessageEvent["data"] = parseResponse<unknown>(
                    data,
                    response.status,
                    contentType,
                    eventType,
                );

                // If the parsed data indicates a failure, set it as the final response for now but let the stream keep going
                if (parsedData.success === false) {
                    finalResponseData = data;
                }

                const event = { eventType, data: parsedData } satisfies SseMessageEvent;
                options.onMessage(event as TMessageEvent);
            }
        }

        // Keep the last (possibly incomplete) chunk in the buffer for next iteration
        buffer = events[events.length - 1];
    }

    // If the stream ended as "done" but we didn't get the final event,
    // something went wrong and we should return an unknown failure
    if (finalResponseData === undefined) {
        const failureResponse: ApiBaseResponseUnknownFailure = {
            success: false,
            responseStatus: response.status,
            detail: buffer,
            message: "Response stream ended unexpectedly",
        };
        return failureResponse;
    }

    // Otherwise, we got the final event, so stop reading and return it
    await reader.cancel();
    return parseResponse<TResponseData>(finalResponseData, response.status);
}

// Overrides for buildApiRequest to allow for an optional URL which changes the return type
export function buildApiRequest<TResponseData, TBody extends ApiRequestBodyBase | undefined = undefined>(
    options: Omit<ApiRequestOptions<TBody>, "body">,
): (body?: TBody) => Promise<ApiBaseResponse<TResponseData>>;

export function buildApiRequest<TResponseData, TBody extends ApiRequestBodyBase | undefined = undefined>(
    options: Omit<ApiRequestOptions<TBody>, "url" | "body"> & { url: null },
): (url: string, body?: TBody) => Promise<ApiBaseResponse<TResponseData>>;

/**
 * Builds an API request function with the given options
 * @param options - The options to define the API request, excluding the body
 * @returns - A function that takes an optional body and returns a promise that resolves to the response from the server
 * @see apiRequest for details on the options
 */
export function buildApiRequest<TResponseData, TBody extends ApiRequestBodyBase | undefined = undefined>(
    options: Omit<ApiRequestOptions<TBody>, "url" | "body"> & { url: string | null },
):
    | ((url: string, body?: TBody) => Promise<ApiBaseResponse<TResponseData>>)
    | ((body?: TBody) => Promise<ApiBaseResponse<TResponseData>>) {
    const targetUrl = options.url;
    if (targetUrl === null) {
        return (url: string, body?: TBody): Promise<ApiBaseResponse<TResponseData>> => {
            return apiRequest<TResponseData, TBody>({ ...options, url, body });
        };
    }

    return (body?: TBody): Promise<ApiBaseResponse<TResponseData>> => {
        return apiRequest<TResponseData, TBody>({ ...options, url: targetUrl, body });
    };
}

/**
 * Retrieve the 'status' from a response that likely has an error in the form of an ApiProblem, if available
 * @param response The response
 * @returns The status code if present
 */
export function getApiProblemStatus(response: ApiBaseResponse<unknown>): number | null {
    if (!response.data || typeof response.data !== "object") {
        return null; // response is not an object
    }

    if (!("status" in response.data) || typeof response.data.status !== "number") {
        return null; // no status field, or invalid status
    }

    return response.data.status;
}
